import React, { useRef } from 'react';

import { BaseButton, useT } from '@repo/common';
import FileRow from './FileRow';
import { getUploadUrl, uploadToS3 } from '../../../api/jobboard/apply.api';

import { ReactComponent as UploadIcon } from '@repo/common/assets/icons/16px/icon_16px_upload.svg';

const MAX_FILE_SIZE = 50 * 1024 * 1024;

const STATUS_PRIORITY = {
  success: 3,
  failed: 2,
  uploading: 1,
};

const UploadFile = ({ files, setFiles, jobId, $isMobile }) => {
  const t = useT();
  const fileInputRef = useRef(null);
  const safeFiles = Array.isArray(files) ? files : files ? [files] : [];

  const sortedFiles = [...safeFiles].sort((a, b) => {
    const statusDiff = STATUS_PRIORITY[b.status] - STATUS_PRIORITY[a.status];

    if (statusDiff !== 0) return statusDiff;

    return b.createdAt - a.createdAt;
  });

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const uploadFile = async (file, id) => {
    try {
      const { uploadUrl, key } = await getUploadUrl(file.name, jobId);

      await uploadToS3(file, uploadUrl, ({ percent, loaded, total }) => {
        setFiles((prev) =>
          prev.map((f) =>
            f.id === id ? { ...f, progress: percent, loaded, total } : f,
          ),
        );
      });

      setFiles((prev) =>
        prev.map((f) =>
          f.id === id ? { ...f, status: 'success', progress: 100, key } : f,
        ),
      );
    } catch (e) {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === id ? { ...f, status: 'failed', errorType: 'network' } : f,
        ),
      );
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);

    for (const file of newFiles) {
      if (file.size > MAX_FILE_SIZE) {
        setFiles((prev) => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            file,
            status: 'failed',
            errorType: 'size',
            createdAt: Date.now(),
          },
        ]);
        continue;
      }

      const fileItem = {
        id: Date.now() + Math.random(),
        file,
        status: 'uploading',
        progress: 0,
        createdAt: Date.now(),
        errorType: null,
      };

      setFiles((prev) => {
        const newList = [...prev, fileItem];
        uploadFile(file, fileItem.id);

        return newList;
      });
    }
  };

  const handleDeleteFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const retryUpload = (id) => {
    const target = safeFiles.find((f) => f.id === id);

    setFiles((prev) =>
      prev.map((f, i) =>
        f.id === id
          ? {
              ...f,
              status: 'uploading',
              errorType: null,
              progress: 0,
            }
          : f,
      ),
    );

    uploadFile(target.file, id);
  };

  return (
    <div className='flex w-full flex-col justify-center items-center text-center'>
      <div
        className={`group flex w-full h-[140px] flex-col justify-center items-center text-center gap-2 rounded-lg border-[1.6px] border-dashed border-grayscale-gray03
    hover:bg-surface-gray10 ${files.length !== 0 && 'mb-4'}`}
      >
        <input
          ref={fileInputRef}
          type='file'
          accept='.jpg,.jpeg,.png,.pdf,.mp4'
          className='hidden'
          onChange={handleFileChange}
        />

        <BaseButton
          type='text'
          size={$isMobile ? 's' : 'm'}
          bgclass='bg-grayscale-gray00'
          borderclass='border-grayscale-gray02'
          hoverclass='group-hover:text-grayscale-gray07 group-hover:bg-grayscale-gray01'
          icon={<UploadIcon />}
          onClick={handleUploadClick}
        >
          {t('Upload', '업로드')}
        </BaseButton>

        <div className='flex flex-col gap-0.5 px-2 tabletH:px-0'>
          <div className='text-text-secondary typo-b13Strong tabletH:typo-b14Strong'>
            {t(
              'Upload a portfolio or cover letter file to strengthen',
              '포트폴리오 또는 자기소개서 파일을 업로드하여',
            )}
            {$isMobile && <br />}
            {t('your application', '지원서를 강화해 보세요')}
          </div>
          <div className='text-text-placeholder typo-b11 tabletH:typo-b12'>
            {t('JPEG, PNG, PDF, and MP4 formats, up to 50MB', 'JPEG, PNG, PDF, MP4 형식, 최대 50MB 지원')}
          </div>
        </div>
      </div>

      <div className='flex flex-col gap-2 w-full'>
        {sortedFiles.map((item) => (
          <FileRow
            key={item.id}
            fileData={item}
            onReset={() => retryUpload(item.id)}
            onDelete={() => handleDeleteFile(item.id)}
            $isMobile={$isMobile}
          />
        ))}
      </div>
    </div>
  );
};

export default UploadFile;
